import type { CdkDragDrop, CdkDragStart } from '@angular/cdk/drag-drop';
import {
  CdkDrag,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { SlicePipe } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  input,
  output,
  Renderer2,
  RendererStyleFlags2,
  signal,
  ViewChild,
  inject,
} from '@angular/core';

import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import type { DisqualifyReason } from '@core/disqualify-reasons-types';
import { DisqualifyReasonsDropdownTemplateComponent } from '@core/disqualify-reasons-ui';
import { EvaluationInsight } from '@core/evaluation-insights-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { HiddenResultPreview, InterviewResult } from '@core/interview-results-types';
import type { Location } from '@core/locations-types';
import type { Placement } from '@core/placements-types';
import {
  FeatureTooltipDirectiveConfig,
  FeatureTooltipWrapperDirective,
} from '@core/product-updates-legacy';
import type { Stage } from '@core/stages-types';
import { TellentBrand } from '@core/tellent-types';
import { Theme } from '@core/theme-util';
import {
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  IconComponent,
  NestedTemplateTriggerDirective,
} from '@core/user-interface-legacy';
import { ComponentPositionPair, ComponentTooltipPosition } from '@core/user-interface-types';
import { deepClone, groupByKey } from '@core/utils-legacy';

import type {
  DisqualifyEvent,
  PlacementActionsCollection,
  PlacementEvent,
  PlacementOpeningIssueCollection,
  ProceedEvent,
  ShowAutomatedActionDetailsEvent,
  UpdatePlacementPositionsEvent,
} from './types';
import { getNextStage } from './utils';
import { CandidateDisqualifiedTooltipComponent } from '../candidate-disqualified-tooltip/candidate-disqualified-tooltip.component';
import { CandidateOfferTooltipComponent } from '../candidate-offer-tooltip/candidate-offer-tooltip.component';
import { CandidateProfilePlacementComponent } from '../candidate-profile-placement/candidate-profile-placement.component';
import { MorePlacementsToggleComponent } from './more-placements-toggle/more-placements-toggle.component';

@Component({
  selector: 'rt-candidate-profile-placements',
  templateUrl: './candidate-profile-placements.component.html',
  styleUrls: ['./candidate-profile-placements.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CandidateProfilePlacementComponent,
    DropdownComponent,
    DropdownItemComponent,
    IconComponent,
    TranslatePipe,
    MorePlacementsToggleComponent,
    SlicePipe,
    CdkDrag,
    CdkDropList,
    CdkDragHandle,
    CdkDragPlaceholder,
    NestedTemplateTriggerDirective,
    DisqualifyReasonsDropdownTemplateComponent,
    CandidateOfferTooltipComponent,
    CandidateDisqualifiedTooltipComponent,
    FeatureTooltipWrapperDirective,
  ],
  host: {
    '[class.has-show-more-toggle]': 'hasShowMoreToggle()',
  },
})
export class CandidateProfilePlacementsComponent {
  private readonly renderer = inject(Renderer2);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18n = inject(I18nService);

  public readonly DropdownWidth = DropdownWidth;
  public readonly placementLimit = 4;

  public placements = input.required<Placement[]>();

  public stages = input.required<Stage[]>();

  public placementActions = input.required<PlacementActionsCollection>();
  public stageAutomationsFetchHandler = input<(pipelineTemplateId: number) => void>();
  public canManageCandidates = input.required<boolean>();

  public disqualifyReasons = input.required<DisqualifyReason[]>();

  public locations = input.required<Location[]>();

  public loadingPlacementIds = input<number[]>([]);

  public showDragHandles = input(false, { transform: booleanAttribute });

  public placementOpeningIssues = input<PlacementOpeningIssueCollection>({});

  public interviewResults = input.required<Record<number, InterviewResult[]>>();
  public showCandidateInAnotherProcessTooltip = input<boolean>();

  public hiddenResultPreviews = input.required<
    Record<number, HiddenResultPreview[]>,
    HiddenResultPreview[]
  >({
    transform: (previews: HiddenResultPreview[]) => groupByKey(previews, 'offerId'),
  });
  public evaluationInsights = input.required<EvaluationInsight[]>();
  public evaluationScale = input<RatingScale | null>(null);
  public hasEvaluationImprovements = input<boolean>(false);
  public theme = input<Theme | null>(null);

  public showMoreToggleValue = signal(false);

  /**
   * Key is placement id, value is array of associated stages.
   */
  public placementStages = computed(() =>
    this.placements().reduce(
      (acc, placement) => ({
        ...acc,
        [placement.id]: this.stages()
          .filter(stage => stage.pipelineTemplateId === placement.offer?.pipelineTemplateId)
          .sort((a, b) => a.position - b.position),
      }),
      <Record<number, Stage[]>>{},
    ),
  );

  public candidateInAnotherProcessTooltipConfig: FeatureTooltipDirectiveConfig = {
    id: 'candidate_in_another_process_tooltip',
    flagName: 'candidateInAnotherProcessTooltipUsed',
    isNew: false,
    brand: TellentBrand.Tellent,
    title: this.i18n.translate(
      'app.hire_assistant.feature_discovery.candidate_in_another_process_tooltip.title',
    ),
    description: this.i18n.translate(
      'app.hire_assistant.feature_discovery.candidate_in_another_process_tooltip.description',
    ),
    ctaText: this.i18n.translate('app.common.got_it'),
    ctaPosition: 'right',
    withPush: false,
    positions: {
      [ComponentTooltipPosition.Left]: new ComponentPositionPair(
        { originX: 'start', originY: 'top' },
        { overlayX: 'end', overlayY: 'top' },
        'topright',
        17,
        -5,
      ),
    },
  };
  public hasShowMoreToggle = computed(() => this.placements().length > this.placementLimit);

  public hasMultiplePlacements = computed(() => this.placements().length > 1);

  public remainingPlacementsCount = computed(() => {
    if (this.showMoreToggleValue()) {
      return 0;
    }

    return this.placements().length - this.placementLimit;
  });

  public showCount = computed(() => this.placements().length - this.remainingPlacementsCount());

  public placementNextStages = computed(() =>
    this.placements().reduce(
      (acc, placement) => ({
        ...acc,
        [placement.id]: getNextStage(placement, this.placementStages()[placement.id]),
      }),
      <Record<number, Stage>>{},
    ),
  );

  public hasPositiveOrHiddenRatings = computed(() =>
    this.placements().some(
      placement => placement.positiveRatings !== null || !placement.ratingVisible,
    ),
  );

  public viewPipeline = output<PlacementEvent>();

  public previewJob = output<PlacementEvent>();

  public remove = output<PlacementEvent>();

  public editHiringDetails = output<PlacementEvent>();

  public updatePlacementPositions = output<UpdatePlacementPositionsEvent>();

  public proceed = output<ProceedEvent>();

  public disqualify = output<DisqualifyEvent>();

  public requalify = output<PlacementEvent>();

  public showAutomatedActionDetails = output<ShowAutomatedActionDetailsEvent>();

  public editDisqualifyReasons = output<void>();

  public editWorkLocation = output<PlacementEvent>();

  public addToJob = output<void>();

  public disqualifyReasonActionsDetails = output<DisqualifyReason>();

  public openEvaluationTab = output<void>();

  @ViewChild('customPlaceholder', { read: ElementRef })
  private customPlaceholder: ElementRef;

  public onDrop($event: CdkDragDrop<Placement>) {
    const { previousIndex, currentIndex } = $event;
    const newPlacements = deepClone(this.placements());

    moveItemInArray(newPlacements, previousIndex, currentIndex);
    this.updatePlacementPositions.emit({
      placement: newPlacements[currentIndex],
      positions: newPlacements.map((item, index) => ({
        id: item.id,
        position: index + 1,
      })),
    });
  }

  public onDragStart($event: CdkDragStart) {
    this.cdRef.detectChanges();
    const draggedElementHeight = $event.source.element.nativeElement.offsetHeight;
    this.renderer.setStyle(
      this.customPlaceholder.nativeElement,
      'height',
      `${draggedElementHeight}px`,
    );
    this.renderer.setStyle(document.body, 'cursor', 'grabbing', RendererStyleFlags2.Important);
  }

  public onDragEnded() {
    this.renderer.removeStyle(document.body, 'cursor');
  }

  public preventCloseOnBackAction($event: MouseEvent): void {
    $event.preventDefault();
  }
}
